"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/utils/cn";
import { forwardRef } from "react";
import { Input as AriaInput, InputProps as AriaInputProps, NumberField as AriaNumberField, NumberFieldProps as AriaNumberFieldProps, ValidationResult as AriaValidationResult, composeRenderProps, Label, Text } from "react-aria-components";
import currencify from "@/utils/currencify";
import { FieldError, FieldGroup } from "./field";
interface CurrencyFieldProps extends Omit<AriaNumberFieldProps, "formatOptions"> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  currency?: string;
  locale?: string;
  showSymbol?: boolean;
  placeholder?: string;
  className?: string;
  showHelperText?: boolean;
  isDisabled?: boolean;
  onDirectInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  currencifyOptions?: {
    abbreviated?: boolean;
    short?: boolean;
    numberWithCommas?: boolean;
    fullWords?: boolean;
    roundOff?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    toFixedDigits?: boolean;
  };
}
const CurrencyFieldInput = forwardRef<HTMLInputElement, AriaInputProps>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = composeRenderProps(className, _temp);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <AriaInput ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
CurrencyFieldInput.displayName = "CurrencyFieldInput";
export const CurrencyField = t0 => {
  const $ = _c(38);
  let className;
  let errorMessage;
  let handleDirectInput;
  let isDisabled;
  let label;
  let placeholder;
  let props;
  let showSymbol;
  let t1;
  if ($[0] !== t0) {
    const {
      label: t2,
      errorMessage: t3,
      currency: t4,
      locale: t5,
      showSymbol: t6,
      placeholder: t7,
      className: t8,
      showHelperText: t9,
      currencifyOptions: t10,
      isDisabled: t11,
      ...t12
    } = t0;
    label = t2;
    errorMessage = t3;
    className = t8;
    isDisabled = t11;
    props = t12;
    t4 === undefined ? "INR" : t4;
    t5 === undefined ? "en-IN" : t5;
    showSymbol = t6 === undefined ? true : t6;
    placeholder = t7 === undefined ? "" : t7;
    const showHelperText = t9 === undefined ? true : t9;
    const currencifyOptions = t10 === undefined ? {
      abbreviated: true
    } : t10;
    handleDirectInput = e => {
      const rawValue = e.target.value.replace(/,/g, "");
      const inputValue = parseFloat(rawValue);
      if (!isNaN(inputValue)) {
        props.onChange?.(inputValue);
      }
    };
    t1 = showHelperText && props.value ? currencify({
      amount: props.value,
      ...currencifyOptions
    }) : "";
    $[0] = t0;
    $[1] = className;
    $[2] = errorMessage;
    $[3] = handleDirectInput;
    $[4] = isDisabled;
    $[5] = label;
    $[6] = placeholder;
    $[7] = props;
    $[8] = showSymbol;
    $[9] = t1;
  } else {
    className = $[1];
    errorMessage = $[2];
    handleDirectInput = $[3];
    isDisabled = $[4];
    label = $[5];
    placeholder = $[6];
    props = $[7];
    showSymbol = $[8];
    t1 = $[9];
  }
  const helperText = t1;
  let t2;
  if ($[10] !== className) {
    t2 = composeRenderProps(className, _temp2);
    $[10] = className;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    };
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== label) {
    t4 = label && <Label className={cn("text-title-medium")}>{label}</Label>;
    $[13] = label;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== showSymbol) {
    t5 = showSymbol && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground z-30 pointer-events-none">₹</div>;
    $[15] = showSymbol;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = showSymbol ? "pl-8 pr-3" : "px-3";
  let t7;
  if ($[17] !== t6) {
    t7 = cn("h-10 w-full rounded-md border border-input bg-background py-2 text-sm ring-offset-background", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", "disabled:cursor-not-allowed disabled:opacity-50", t6);
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== handleDirectInput || $[20] !== placeholder || $[21] !== t7) {
    t8 = <CurrencyFieldInput placeholder={placeholder} onInput={handleDirectInput} className={t7} />;
    $[19] = handleDirectInput;
    $[20] = placeholder;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== isDisabled || $[24] !== t5 || $[25] !== t8) {
    t9 = <FieldGroup className="relative" variant="ghost" isDisabled={isDisabled}>{t5}{t8}</FieldGroup>;
    $[23] = isDisabled;
    $[24] = t5;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== helperText) {
    t10 = helperText && <Text slot="description" className="text-sm text-muted-foreground">{helperText}</Text>;
    $[27] = helperText;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== errorMessage) {
    t11 = <FieldError>{errorMessage}</FieldError>;
    $[29] = errorMessage;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== props || $[32] !== t10 || $[33] !== t11 || $[34] !== t2 || $[35] !== t4 || $[36] !== t9) {
    t12 = <AriaNumberField className={t2} formatOptions={t3} {...props}>{t4}{t9}{t10}{t11}</AriaNumberField>;
    $[31] = props;
    $[32] = t10;
    $[33] = t11;
    $[34] = t2;
    $[35] = t4;
    $[36] = t9;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
export type { CurrencyFieldProps };
function _temp(className_0) {
  return cn("w-full min-w-0 flex-1 bg-background px-3 py-2 outline-0 placeholder:text-muted-foreground", "text-sm font-medium border-0 focus:border-0 focus:ring-0", className_0);
}
function _temp2(className_0) {
  return cn("group flex flex-col gap-2", className_0);
}